import { Controller } from "@hotwired/stimulus"
import { createAlert, clearAlerts } from "../scripts/createAlert"

export default class extends Controller {
  static targets = [
    "pageWrap",
    "pageImage",
    "overlay",
    "pageLabel",
    "previousButton",
    "nextButton",
    "bucketList",
    "newBucketName",
    "recordSelect",
    "normalizeButton"
  ]

  static values = {
    extractUrl: String,
    bucketsUrl: String,
    pages: Array,
    buckets: Array
  }

  connect() {
    this.currentPage = 1
    this.activeBucketId = this.bucketsValue[0] ? this.bucketsValue[0].id : null
    this.dragging = null
    this.overlayContext = this.overlayTarget.getContext("2d")

    this.pageImageTarget.addEventListener("load", () => {
      this.sizeOverlay()
      this.drawBoxes()
    })

    this.overlayTarget.addEventListener("mousedown", (event) => this.startDrawing(event))
    this.overlayTarget.addEventListener("mousemove", (event) => this.updateDrawing(event))
    window.addEventListener("mouseup", () => this.finishDrawing())
    window.addEventListener("resize", () => {
      this.sizeOverlay()
      this.drawBoxes()
    })

    this.showPage(1)
    this.renderBuckets()
    this.syncRecordSelectOptions()
    this.toggleNormalizeButton()
  }

  previousPage() {
    this.showPage(this.currentPage - 1)
  }

  nextPage() {
    this.showPage(this.currentPage + 1)
  }

  showPage(pageNumber) {
    const pages = this.pagesValue
    if (pages.length === 0) return

    this.currentPage = Math.min(Math.max(1, pageNumber), pages.length)
    const page = pages[this.currentPage - 1]
    this.pageLabelTarget.textContent = `${this.currentPage} / ${pages.length}`
    this.previousButtonTarget.disabled = this.currentPage === 1
    this.nextButtonTarget.disabled = this.currentPage === pages.length
    this.pageImageTarget.src = page.url
  }

  sizeOverlay() {
    const displayedWidth = this.pageImageTarget.clientWidth
    const displayedHeight = this.pageImageTarget.clientHeight
    this.overlayTarget.width = displayedWidth
    this.overlayTarget.height = displayedHeight
    this.overlayTarget.style.width = `${displayedWidth}px`
    this.overlayTarget.style.height = `${displayedHeight}px`
  }

  displayScale() {
    if (!this.pageImageTarget.clientWidth) return 1
    return this.pageImageTarget.naturalWidth / this.pageImageTarget.clientWidth
  }

  startDrawing(event) {
    if (!this.activeBucketId) {
      createAlert("danger", "Select a record first.", true)
      return
    }

    // Record is active now — drop any leftover "Select a record first" toast.
    clearAlerts()

    const pointer = this.pointerPosition(event)
    this.dragging = { x0: pointer.x, y0: pointer.y, x: pointer.x, y: pointer.y }
    event.preventDefault()
  }

  updateDrawing(event) {
    if (!this.dragging) return

    const pointer = this.pointerPosition(event)
    this.dragging.x = pointer.x
    this.dragging.y = pointer.y
    this.drawBoxes(this.normalizedRect(this.dragging))
  }

  async finishDrawing() {
    if (!this.dragging) return

    const rect = this.normalizedRect(this.dragging)
    this.dragging = null

    if (rect.w < 3 || rect.h < 3) {
      this.drawBoxes()
      return
    }

    const scale = this.displayScale()
    const box = {
      page: this.currentPage,
      x: Math.round(rect.x * scale),
      y: Math.round(rect.y * scale),
      w: Math.round(rect.w * scale),
      h: Math.round(rect.h * scale)
    }

    await this.extractBox(box)
  }

  pointerPosition(event) {
    const bounds = this.overlayTarget.getBoundingClientRect()
    return {
      x: Math.min(Math.max(0, event.clientX - bounds.left), this.overlayTarget.width),
      y: Math.min(Math.max(0, event.clientY - bounds.top), this.overlayTarget.height)
    }
  }

  normalizedRect(drag) {
    return {
      x: Math.min(drag.x0, drag.x),
      y: Math.min(drag.y0, drag.y),
      w: Math.abs(drag.x - drag.x0),
      h: Math.abs(drag.y - drag.y0)
    }
  }

  drawBoxes(liveRect) {
    const context = this.overlayContext
    context.clearRect(0, 0, this.overlayTarget.width, this.overlayTarget.height)
    const scale = this.displayScale()

    this.bucketsValue.forEach((bucket) => {
      const isActive = bucket.id === this.activeBucketId
      ;(bucket.boxes || []).forEach((box, index) => {
        if (box.page !== this.currentPage) return

        const x = box.x / scale
        const y = box.y / scale
        const width = box.w / scale
        const height = box.h / scale
        context.lineWidth = isActive ? 2.5 : 1.5
        context.strokeStyle = bucket.color
        context.fillStyle = this.hexToRgba(bucket.color, 0.14)
        context.fillRect(x, y, width, height)
        context.strokeRect(x, y, width, height)

        const label = (bucket.boxes || []).length > 1 ? `${bucket.name} #${index + 1}` : bucket.name
        context.font = "12px sans-serif"
        const labelWidth = context.measureText(label).width + 8
        context.fillStyle = bucket.color
        context.fillRect(x, Math.max(0, y - 16), labelWidth, 16)
        context.fillStyle = "#111827"
        context.fillText(label, x + 4, Math.max(11, y - 4))
      })
    })

    if (liveRect) {
      context.lineWidth = 2
      context.setLineDash([5, 4])
      context.strokeStyle = this.activeBucketColor()
      context.strokeRect(liveRect.x, liveRect.y, liveRect.w, liveRect.h)
      context.setLineDash([])
    }
  }

  activeBucketColor() {
    const bucket = this.bucketsValue.find((candidate) => candidate.id === this.activeBucketId)
    return bucket ? bucket.color : "#2563eb"
  }

  hexToRgba(hex, alpha) {
    const value = hex.replace("#", "")
    const red = parseInt(value.substring(0, 2), 16)
    const green = parseInt(value.substring(2, 4), 16)
    const blue = parseInt(value.substring(4, 6), 16)
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`
  }

  selectBucket(event) {
    this.activeBucketId = event.currentTarget.dataset.bucketId
    clearAlerts()
    this.renderBuckets()
    this.drawBoxes()
  }

  async addBucket() {
    const name = this.selectedBucketName()
    if (!name) return

    if (this.bucketsValue.some((bucket) => bucket.name === name)) {
      createAlert("danger", "That record is already in the list.", true)
      this.clearBucketNameInput()
      return
    }

    const colors = ["#2563eb", "#198754", "#fd7e14", "#6f42c1", "#dc3545", "#0dcaf0"]
    const bucket = {
      id: `bucket-${Date.now()}`,
      name: name,
      color: colors[this.bucketsValue.length % colors.length],
      boxes: []
    }

    // Newest record at the top so the SID does not have to scroll to find it.
    this.bucketsValue = [bucket, ...this.bucketsValue]
    this.activeBucketId = bucket.id
    clearAlerts()
    this.clearBucketNameInput()
    this.renderBuckets()
    this.syncRecordSelectOptions()
    await this.persistBuckets()
  }

  selectedBucketName() {
    if (this.hasRecordSelectTarget) {
      return this.recordSelectTarget.value.trim()
    }

    if (this.hasNewBucketNameTarget) {
      return this.newBucketNameTarget.value.trim()
    }

    return ""
  }

  clearBucketNameInput() {
    if (this.hasRecordSelectTarget) {
      this.recordSelectTarget.value = ""
    }

    if (this.hasNewBucketNameTarget) {
      this.newBucketNameTarget.value = ""
    }
  }

  // Disable dropdown options that are already buckets so the same record cannot be added twice.
  syncRecordSelectOptions() {
    if (!this.hasRecordSelectTarget) return

    const addedNames = new Set(this.bucketsValue.map((bucket) => bucket.name))
    Array.from(this.recordSelectTarget.options).forEach((option) => {
      if (!option.value) return

      option.disabled = addedNames.has(option.value)
    })
  }

  async deleteBox(event) {
    const bucketId = event.currentTarget.dataset.bucketId
    const boxId = event.currentTarget.dataset.boxId
    this.bucketsValue = this.bucketsValue.map((bucket) => {
      if (bucket.id !== bucketId) return bucket
      return {
        ...bucket,
        boxes: (bucket.boxes || []).filter((box) => box.id !== boxId)
      }
    })
    this.renderBuckets()
    this.drawBoxes()
    this.toggleNormalizeButton()
    await this.persistBuckets()
  }

  async removeBucket(event) {
    const bucketId = event.currentTarget.dataset.bucketId
    this.bucketsValue = this.bucketsValue.filter((bucket) => bucket.id !== bucketId)
    if (this.activeBucketId === bucketId) {
      this.activeBucketId = this.bucketsValue[0] ? this.bucketsValue[0].id : null
    }
    this.renderBuckets()
    this.syncRecordSelectOptions()
    this.drawBoxes()
    this.toggleNormalizeButton()
    await this.persistBuckets()
  }

  // Same contenteditable + blur persist pattern as transcription chunk editing.
  async updateBoxText(event) {
    const bucketId = event.currentTarget.dataset.bucketId
    const boxId = event.currentTarget.dataset.boxId
    const text = event.currentTarget.innerText

    this.bucketsValue = this.bucketsValue.map((bucket) => {
      if (bucket.id !== bucketId) return bucket
      return {
        ...bucket,
        boxes: (bucket.boxes || []).map((box) => {
          if (box.id !== boxId) return box
          return { ...box, text: text }
        })
      }
    })
    this.toggleNormalizeButton()
    await this.persistBuckets()
  }

  renderBuckets() {
    this.bucketListTarget.innerHTML = this.bucketsValue.map((bucket) => {
      const isActive = bucket.id === this.activeBucketId
      const boxes = bucket.boxes || []
      const boxRows = boxes.map((box, index) => `
        <div class="mb-2">
          <div class="d-flex flex-row justify-content-between align-items-center small mb-1">
            <span class="d-flex flex-row align-items-center">
              <span>Page ${box.page}${boxes.length > 1 ? ` #${index + 1}` : ""}</span>
              <i class="far fa-edit color-fanword-text ms-2" aria-hidden="true" title="Editable"></i>
            </span>
            <button type="button" class="btn btn-link p-0 text-decoration-none" data-action="click->record-book-buckets#deleteBox" data-bucket-id="${bucket.id}" data-box-id="${box.id}" aria-label="Remove">
              <i class="fas fa-trash-alt color-fanword-text"></i>
            </button>
          </div>
          <div
            class="record-book-bucket-text fanword-border fanword-border-radius p-2 bg-fanword-white"
            contenteditable="true"
            data-action="blur->record-book-buckets#updateBoxText"
            data-bucket-id="${bucket.id}"
            data-box-id="${box.id}"
          >${this.escapeHtml(box.text || "")}</div>
        </div>
      `).join("")

      return `
        <div class="fanword-border fanword-border-radius p-3 mb-3 ${isActive ? "bg-fanword-background" : "bg-fanword-white"}">
          <div class="d-flex flex-row justify-content-between align-items-start">
            <button type="button" class="btn p-0 border-0 text-start font-fanword-bold" data-action="click->record-book-buckets#selectBucket" data-bucket-id="${bucket.id}">
              <span class="record-book-bucket-swatch me-2" style="background:${bucket.color};"></span>
              ${this.escapeHtml(bucket.name)}
            </button>
            <button type="button" class="btn btn-link p-0 text-decoration-none" data-action="click->record-book-buckets#removeBucket" data-bucket-id="${bucket.id}" aria-label="Remove record">
              <i class="fas fa-trash-alt color-fanword-text"></i>
            </button>
          </div>
          <div class="mt-2">${boxRows || "<p class='text-muted small mb-0'>No boxes yet</p>"}</div>
        </div>
      `
    }).join("")
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  toggleNormalizeButton() {
    if (!this.hasNormalizeButtonTarget) return

    const hasExtractedText = this.bucketsValue.some((bucket) => {
      return (bucket.boxes || []).some((box) => String(box.text || "").trim().length > 0)
    })
    this.normalizeButtonTarget.classList.toggle("d-none", !hasExtractedText)
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  async extractBox(box) {
    try {
      const response = await fetch(this.extractUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({
          bucket_id: this.activeBucketId,
          page: box.page,
          x: box.x,
          y: box.y,
          w: box.w,
          h: box.h
        })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Could not extract text")

      this.bucketsValue = data.buckets
      this.renderBuckets()
      this.syncRecordSelectOptions()
      this.drawBoxes()
      this.toggleNormalizeButton()
    } catch (error) {
      createAlert("danger", error.message, true)
      this.drawBoxes()
    }
  }

  async persistBuckets() {
    try {
      const response = await fetch(this.bucketsUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ buckets: this.bucketsValue })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || "Could not save records")
      this.bucketsValue = data.buckets
      this.syncRecordSelectOptions()
    } catch (error) {
      createAlert("danger", error.message, true)
    }
  }
}
